import React, { useMemo, useState, useEffect } from "react";
import BuildCartState from "./BuildCartState";
import { getBrandNavigationData } from "@/utils/api";
import { useAppSelector } from "@/hooks/useAppSelector";
import { motion } from "framer-motion";

const BuildYourCart: React.FC = () => {
  const { cart } = useAppSelector((state) => state.cart);
  const { selectedBrand } = useAppSelector((state) => state.steps);
  const { manufacturers } = useAppSelector((state) => state.home);

  const brandData = useMemo(
    () => getBrandNavigationData(cart, selectedBrand, manufacturers),
    [cart, selectedBrand, manufacturers]
  );


  // Store previous values
  const [prevValues, setPrevValues] = useState({
    BeforeMOQ: brandData.BeforeMOQ,
    MOQProgress: brandData.MOQProgress,
    DiscountProgress: brandData.DiscountProgress,
  });

  useEffect(() => {
    setPrevValues({
      BeforeMOQ: brandData.BeforeMOQ,
      MOQProgress: brandData.MOQProgress,
      DiscountProgress: brandData.DiscountProgress,
    });
  }, [brandData]);

  // Determine delays based on increase or decrease
  const getDelay = (key: "BeforeMOQ" | "MOQProgress" | "DiscountProgress") => {
    if (brandData[key] > prevValues[key]) {
      // Filling order: First -> Second -> Third
      return key === "BeforeMOQ" ? 0 : key === "MOQProgress" ? 0.3 : 0.6;
    } else if (brandData[key] < prevValues[key]) {
      // Reduction order: Third -> Second -> First
      return key === "DiscountProgress" ? 0 : key === "MOQProgress" ? 0.3 : 0.6;
    }
    return 0;
  };

  return (
    <div className="w-full h-[65px] flex flex-col items-start build-your-cart">
      {/* <span className="text-black-1 font-bold text-[16px] mb-3">
        Build Your Cart
      </span> */}
      <div className="relative h-full w-full">
        <div className="flex w-full items-start">
          {/* Red Section (Before MOQ) */}
          <div className="w-1/3 flex items-center relative">
            <div
              className="h-[6px] w-full relative bg-[#ffcccc] overflow-hidden"
              style={{
                clipPath: "polygon(1.3% 0, 100% 0, 98.7% 100%, 0 100%)",
              }}
            >
               <div
                className="absolute z-20 w-[8px] h-full top-0 left-1/3 bg-white-1"
                style={{
                  clipPath: "polygon(50% 0%, 100% 0%, 50% 100%, 0% 100%)",
                }}
              />
               <div
                className="absolute z-20 w-[8px] h-full top-0 left-2/3 bg-white-1"
                style={{
                  clipPath: "polygon(50% 0%, 100% 0%, 50% 100%, 0% 100%)",
                }}
              />
              <motion.div
                className="absolute top-0 left-0 h-full bg-[#ba3737]"
                style={{
                  clipPath: "polygon(0% 0%, 100% 0%, 98.7% 100%, 0% 100%)",
                }}
                initial={{ width: `${prevValues.BeforeMOQ * 100}%` }}
                animate={{ width: `${brandData.BeforeMOQ * 100}%` }}
                transition={{
                  duration: 1.5,
                  ease: "easeInOut",
                  delay: getDelay("BeforeMOQ"),
                }}
              />
            </div>
          </div>

          {/* Gray Section (MOQ Progress) */}
          <div className="w-1/3 flex items-center">
            <div className="w-[6px] h-[6px] relative">
              <BuildCartState {...brandData.MOQState} />
            </div>
            <div
              className={`h-[6px] w-full relative overflow-hidden ${
                !brandData.discountPrice ? "hidden" : "block"
              } bg-[#d3d3d3]`}
              style={{
                clipPath: "polygon(1.3% 0, 100% 0, 98.7% 100%, 0 100%)",
              }}
            >
              <div
                className="absolute z-20 w-[8px] h-full top-0 left-1/3 bg-white-1"
                style={{
                  clipPath: "polygon(50% 0%, 100% 0%, 50% 100%, 0% 100%)",
                }}
              />
               <div
                className="absolute z-20 w-[8px] h-full top-0 left-2/3 bg-white-1"
                style={{
                  clipPath: "polygon(50% 0%, 100% 0%, 50% 100%, 0% 100%)",
                }}
              />
              <motion.div
                className="absolute top-0 left-0 h-full bg-[#283248]"
                style={{
                  clipPath: "polygon(0% 0%, 100% 0%, 98.7% 100%, 0% 100%)",
                }}
                initial={{ width: `${prevValues.MOQProgress * 100}%` }}
                animate={{ width: `${brandData.MOQProgress * 100}%` }}
                transition={{
                  duration: 1.5,
                  ease: "easeInOut",
                  delay: getDelay("MOQProgress"),
                }}
              />
            </div>
          </div>

          {/* Green Section (Discount Progress) */}
          {brandData.discountPrice && (
            <div className="w-1/3 flex items-center relative">
              <div className="w-[6px] h-[6px] relative">
                <BuildCartState {...brandData.DiscountState} />
              </div>
              <div
                className="h-[6px] w-full relative bg-[#a3d9a5] overflow-hidden"
                style={{
                  clipPath: "polygon(1.3% 0, 100% 0, 98.7% 100%, 0 100%)",
                }}
              >
                <div
                className="absolute z-20 w-[8px] h-full top-0 left-1/3 bg-white-1"
                style={{
                  clipPath: "polygon(50% 0%, 100% 0%, 50% 100%, 0% 100%)",
                }}
              />
               <div
                className="absolute z-20 w-[8px] h-full top-0 left-2/3 bg-white-1"
                style={{
                  clipPath: "polygon(50% 0%, 100% 0%, 50% 100%, 0% 100%)",
                }}
              />
                <motion.div
                  className="absolute top-0 left-0 h-full bg-[#28a447]"
                  style={{
                    clipPath: "polygon(0% 0%, 100% 0%, 98.7% 100%, 0% 100%)",
                  }}
                  initial={{ width: `${prevValues.DiscountProgress * 100}%` }}
                  animate={{ width: `${brandData.DiscountProgress * 100}%` }}
                  transition={{
                    duration: 1.5,
                    ease: "easeInOut",
                    delay: getDelay("DiscountProgress"),
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuildYourCart;
